import { FormInstance } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { confirm } from 'components/atoms/modal';
import { Option } from 'components/atoms/select';
import { useTranslate } from 'hooks/use-translation';
import { useUsersQuery } from 'queries/members';
import { useGetAllPartnerCompanies } from 'queries/partner';
import { byLabel } from 'utils/comparators/by-label';
import { UserOption } from 'utils/project-fields/types';
import { ProjectData } from './types';
import {
  companyIdFromPartnerCompanyValue,
  isPartnerCompanyValue,
} from './utils';
import { VendorTeamStepFormValues } from './vendor-team-step';

interface Props {
  projectData: ProjectData;
  onValuesChange: (values: any, allValues: unknown) => void;
  vendorStepform: FormInstance<VendorTeamStepFormValues>;
}
interface Result {
  partnersAvailable: boolean;
  partnersLoading: boolean;
  partnerOptions: Option[];
  addPartners: (companyOption: UserOption[]) => void;
  removePartner: (companyOption: UserOption) => void;
}

export const useManagePartners = ({
  projectData,
  onValuesChange,
  vendorStepform: form,
}: Props): Result => {
  const translate = useTranslate();
  const { partners } = projectData;
  const { data: allPartnerCompanies, isLoading } = useGetAllPartnerCompanies();
  const { data: users = [] } = useUsersQuery();
  const partnerOptions: Option[] = useMemo(
    () =>
      (allPartnerCompanies ?? [])
        .filter(({ active }) => active)
        .map(({ companyName, companyId, companyLogoUrl }) => ({
          label: companyName,
          value: companyId,
          imageUrl: companyLogoUrl,
        }))
        .sort(byLabel),
    [allPartnerCompanies],
  );
  const addPartners = useCallback(
    (partnerCompanyOptions) => {
      const newPartnerlist = {
        partners: (partners ?? []).concat(partnerCompanyOptions),
      };

      onValuesChange(newPartnerlist, {
        ...projectData,
        ...newPartnerlist,
      });
    },
    [onValuesChange, partners, projectData],
  );

  const removePartner = useCallback(
    (partnerCompanyOption) => {
      const placeholders = form.getFieldValue('internalPlaceholders');

      const newPartnerList = (partners ?? []).filter(
        (x) => x.value !== partnerCompanyOption.value,
      );

      form.setFieldsValue({
        internalPlaceholders: (placeholders ?? []).map((placeholder) => {
          const { user, ...rest } = placeholder;

          if (!placeholder.user) {
            return placeholder;
          }
          if (!isPartnerCompanyValue(`${placeholder.user.value}`)) {
            if (
              users.find(({ userId }) => userId === placeholder.user.value)
                ?.company.companyId === partnerCompanyOption.value
            ) {
              return { ...rest, user: null };
            }

            return placeholder;
          }
          const companyId = companyIdFromPartnerCompanyValue(
            `${placeholder.user.value}`,
          );

          if (companyId === partnerCompanyOption.value) {
            return { ...rest, user: null };
          }

          return placeholder;
        }),
      });
      const _internalUsers = form.getFieldValue('internalUsers');

      form.setFieldsValue({
        internalUsers: (_internalUsers ?? []).filter(
          ({ value }) =>
            users.find(({ userId }) => userId === value)?.company.companyId !==
            partnerCompanyOption.value,
        ),
      });
      onValuesChange(
        {
          partners: newPartnerList,
        },
        {
          ...form.getFieldsValue(),
          partners: newPartnerList,
        },
      );
    },
    [form, onValuesChange, partners, users],
  );
  const checkAndRemovePartner = useCallback(
    (partnerCompanyOption) => {
      const placeholders = form.getFieldValue('internalPlaceholders');

      const isPartnerCompanyAssociatedWithPlaceholder = !!(
        placeholders ?? []
      ).find(({ user }) => {
        if (user == null) {
          return false;
        }
        if (!isPartnerCompanyValue(`${user.value}`)) {
          if (
            users.find(({ userId }) => userId === user.value)?.company
              .companyId === partnerCompanyOption.value
          ) {
            return true;
          }

          return false;
        }
        const companyId = companyIdFromPartnerCompanyValue(`${user.value}`);

        if (companyId === partnerCompanyOption.value) {
          return true;
        }

        return false;
      });

      if (isPartnerCompanyAssociatedWithPlaceholder) {
        const title = translate(
          'projects.create.team_details.remove_partner.confirmation.title',
          { companyName: partnerCompanyOption.label },
        );
        const content = translate(
          'projects.create.team_details.remove_partner.confirmation.message',
          {
            companyName: partnerCompanyOption.label,
            b: (chunks) => <strong>{chunks}</strong>,
          },
        );
        const okButtonText = translate('yes');

        confirm({
          title,
          content,
          okButtonProps: {
            danger: true,
          },
          okText: okButtonText,
          onOk: () => {
            removePartner(partnerCompanyOption);
          },
        });
      } else {
        removePartner(partnerCompanyOption);
      }
    },
    [form, removePartner, translate, users],
  );

  return {
    partnersAvailable: allPartnerCompanies?.length !== 0,
    partnersLoading: isLoading,
    partnerOptions,
    addPartners,
    removePartner: checkAndRemovePartner,
  };
};
